import { authApi, publicApi } from "@api";
import GetCodeButton, { GetCodeButtonRef } from "@components/inputs/GetCodeButton/GetCodeButton";
import PasswordTextField from "@components/inputs/PasswordTextField/PasswordTextField";
import { usePostLoginCheck } from "@components/utils/Auth/hooks";
import { RegisterCallbackProps } from "@components/utils/Auth/types/auth-props";
import { envConfig } from "@configs/env";
import { SEON_FEATURE } from "@configs/seon";

import { OTPMethod, OTPSource, RegistrationMethod, useStatsigTracking } from "@configs/statsig";

import { ValidatorSchema } from "@constants";
import { zodResolver } from "@hookform/resolvers/zod";
import useCaptureFormValidationErrors from "@hooks/useCaptureFormValidationErrors";
import useContactLinks from "@hooks/useContactLinks";
import { useLoginSubmit } from "@hooks/useLoginSubmit";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { useSeonSession } from "@hooks/useSeonSession";
import auth from "@stores/auth";
import { hideOnboardingOverlay, showOnboardingOverlay } from "@stores/ui/slices";
import { persistCredential } from "@utils/credentials";
import { resolveAuthResponseError } from "@utils/error-resolvers";
import { getOTPCodeSentMessage } from "@utils/message";
import {
  DEFAULT_OTP_COOLDOWN_SECONDS,
  getOtpCooldownSecondsFromRateLimitError,
  getOtpRemainingSecondsFromNextRequestAt,
} from "@utils/otp-cooldown";
import { clearReferralCode, getReferralCode } from "@utils/referral";
import {
  APIError,
  APIErrorCodes,
  APIValidationError,
  Button,
  ErrorResponse,
  LoginGrantType,
  QueryError,
  TextField,
  cn,
  standardizePhoneNumber,
  useFieldHelperText,
  useHandleApiResponse,
} from "kz-ui-sdk";
import { get } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { MdMail, MdPerson, MdPhoneAndroid } from "react-icons/md";
import { useDispatch } from "react-redux";
import { z } from "zod";
import { AuthSocial } from "../../AuthSocial";
import PhoneAlreadyRegisteredDialog from "./components/PhoneAlreadyRegisteredDialog";

export type RegisterFormType = {
  username: string;
  password: string;
  phone: string;
  sms: string;
};

const inputClasses = {
  "input&": "w-full !px-0 !pl-11 text-start",
  "iconContainer&": "!left-3 !top-[10px]",
};

const isValidationError = (error: APIError): error is APIValidationError => {
  return error.message === APIErrorCodes.ValidationError;
};

const getErrorMessages = (error: QueryError<ErrorResponse>) => {
  const errorData = error?.data?.error;
  if (errorData && isValidationError(errorData)) {
    return {
      username: errorData?.errors?.accessKey?.msg,
      phone: errorData?.errors?.phone?.msg,
    };
  }
  return {};
};

interface RegisterUPProps extends RegisterCallbackProps {}

const RegisterUP = ({
  onRegisterSuccess,
  onLoginSuccess,
  onPostLoginCheckFailed,
  onPostLoginCheckPassed,
  deferUpdateToken,
}: RegisterUPProps) => {
  const { phoneMinLength, phoneMaxLength, phonePrefix, phoneStartWith, phoneSimpleError, showPhoneInputPrefix } =
    useMemberSettings();
  const { t, i18n } = useTranslation();
  const {
    trackOTPSent,
    trackOTPRequestFailed,
    trackRegisterFormSubmit,
    trackRegistrationComplete,
    trackRegistrationFailed,
    trackOTPVerified,
    trackOTPFailedAttempt,
    trackRegisterFormFocus,
    trackOTPRequest,
  } = useStatsigTracking();
  const dispatch = useDispatch();
  const getCodeButtonRef = useRef<GetCodeButtonRef>(null);
  const otpCooldownUntilRef = useRef<number>(0);
  const formStartTime = useRef<number>(Date.now());
  const otpSentTime = useRef<number | null>(null);
  const hasRegisterFormFocusedRef = useRef<boolean>(false);
  const authTokenRef = useRef<any>(null);
  const postLoginCheck = usePostLoginCheck({
    onLoginSuccess,
    onPostLoginCheckPassed,
    onPostLoginCheckFailed,
    redirectHome: false,
  });
  const [createRegisterMutation, { isLoading: isCreateRegisterLoading }] = publicApi.useCreateRegisterMutation();
  const [verifyOTP] = publicApi.useVerifyOTPMutation();
  const [register] = publicApi.useSubmitRegisterMutation();
  const { handleLogin, isLoading: isLoginLoading } = useLoginSubmit({
    onLoginSuccess: postLoginCheck,
    deferUpdateToken,
    suppressLoginTrack: true,
  });
  const [login] = authApi.useLoginMutation();
  const [getProfileWithToken] = authApi.useLazyGetProfileWithTokenQuery();
  const [isPhoneAlreadyRegisteredDialogOpen, setIsPhoneAlreadyRegisteredDialogOpen] = useState(false);
  const [isRegisterLoading, setIsRegisterLoading] = useState(false);
  const [hasClickedGetCode, setHasClickedGetCode] = useState(false);
  const hasGetCodeHintShownRef = useRef(false);
  const [isGetCodeHintActive, setIsGetCodeHintActive] = useState(false);

  const [existingProfileDetails, setExistingProfileDetails] = useState<{
    accessKey: string;
    phone: string;
    email: string;
  }>({
    accessKey: "",
    phone: "",
    email: "",
  });

  const { getSession: getSeonSession } = useSeonSession(SEON_FEATURE.REGISTER, {
    autoInit: true,
  });

  const { openContact } = useContactLinks();

  const handleOpenContactLink = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      openContact();
    },
    [openContact],
  );

  const registerFormSchema = useMemo(() => {
    return z.object({
      username: ValidatorSchema.username,
      password: ValidatorSchema.registerPassword,
      phone: ValidatorSchema.phone({
        minLength: phoneMinLength,
        maxLength: phoneMaxLength,
        startWith: phoneStartWith,
        simpleError: phoneSimpleError,
        msgType: "digits",
      }),
      sms: ValidatorSchema.otp,
    });
  }, [phoneMinLength, phoneMaxLength, phoneStartWith, phoneSimpleError]);

  const form = useForm<RegisterFormType>({
    mode: "onChange",
    resolver: zodResolver(registerFormSchema),
  });

  const {
    handleSubmit: handleFormSubmit,
    register: formRegister,
    control,
    formState: { errors: formErrors, submitCount },
    trigger,
    getValues,
    setError,
  } = form;

  const [watchedUsername, watchedPassword, watchedPhone] = useWatch({
    control,
    name: ["username", "password", "phone"],
  });

  useEffect(() => {
    if (hasGetCodeHintShownRef.current) return;
    if (!watchedUsername?.trim() || !watchedPassword || !watchedPhone?.trim()) return;

    // Hint user to tap "Get code" once mandatory fields are valid
    trigger(["username", "password", "phone"]).then((isValid) => {
      if (!isValid) return;
      if (hasGetCodeHintShownRef.current) return;

      hasGetCodeHintShownRef.current = true;
      setIsGetCodeHintActive(true);
    });
  }, [trigger, watchedUsername, watchedPassword, watchedPhone]);

  const { handleApiResponse } = useHandleApiResponse({ toast });

  // Capture form validation errors
  useCaptureFormValidationErrors(form, { feature: "register" });

  const fieldHelperTextManager = useFieldHelperText({
    submitCount: submitCount + (hasClickedGetCode ? 1 : 0),
    trigger: trigger,
    errors: formErrors,
  });

  const handleGetCode = useCallback(async () => {
    setHasClickedGetCode(true);

    return new Promise((resolve, reject) => {
      trigger(["username", "password", "phone"]).then((isFormValid) => {
        if (!isFormValid) {
          reject(new Error("Form is not valid"));
          return;
        }

        // Stop hint animation once an OTP request is about to be sent
        setIsGetCodeHintActive(false);

        const { phone, username, password } = getValues();
        trackOTPRequest(OTPMethod.SMS, { source: OTPSource.REGISTER });
        createRegisterMutation({
          phone: standardizePhoneNumber(phone, phonePrefix),
          accountId: envConfig.accountId,
          accessKey: username.toLowerCase(),
          password,
        }).then((response) => {
          handleApiResponse(response, {
            toastSuccess: false,
            toastError: false,
            onSuccess: (otpRes) => {
              // Track OTP sent success with analytics-rich metadata
              trackOTPSent(otpRes.method as OTPMethod, { source: OTPSource.REGISTER });

              toast.success(getOTPCodeSentMessage(otpRes.method, i18n.language, t), { duration: 8000 });
              const nextRequestAt = otpRes.nextRequestAt;
              const remainingSeconds = nextRequestAt
                ? getOtpRemainingSecondsFromNextRequestAt(nextRequestAt)
                : DEFAULT_OTP_COOLDOWN_SECONDS;
              getCodeButtonRef.current?.startTimer(remainingSeconds);
              otpCooldownUntilRef.current = remainingSeconds > 0 ? Date.now() + remainingSeconds * 1000 : 0;
              resolve(otpRes);
            },
            onError: (error) => {
              // If rate-limited, disable "Get code" based on backend cooldown.
              const rateLimitSeconds = getOtpCooldownSecondsFromRateLimitError(error);
              const cooldownSeconds = rateLimitSeconds ?? DEFAULT_OTP_COOLDOWN_SECONDS;
              getCodeButtonRef.current?.startTimer(cooldownSeconds);
              otpCooldownUntilRef.current = cooldownSeconds > 0 ? Date.now() + cooldownSeconds * 1000 : 0;

              const message = resolveAuthResponseError(error, t);
              if (message) {
                toast.error(message);
              }

              trackOTPRequestFailed(error, { source: OTPSource.REGISTER });

              reject(error);
            },
          });
        });
      });
    });
  }, [
    createRegisterMutation,
    getValues,
    handleApiResponse,
    i18n.language,
    phonePrefix,
    setHasClickedGetCode,
    setIsGetCodeHintActive,
    t,
    trackOTPRequest,
    trackOTPRequestFailed,
    trackOTPSent,
    trigger,
  ]);

  const getUserDataForDialog = useCallback(
    async (phone: string, code: string): Promise<boolean> => {
      try {
        const loginResult = await login({
          grant_type: LoginGrantType.OTP,
          phone: standardizePhoneNumber(phone, phonePrefix),
          code: code,
          account: envConfig.accountId,
        });

        if (loginResult.data) {
          const accessToken = loginResult.data.access_token;
          const profile = await getProfileWithToken(accessToken).unwrap();

          authTokenRef.current = loginResult.data;
          setExistingProfileDetails({
            accessKey: get(profile, "credential.accessKey", ""),
            phone: get(profile, "self.member.phone", ""),
            email: get(profile, "self.member.email", ""),
          });
          return true;
        }
      } catch {
        return false;
      }
      return false;
    },
    [getProfileWithToken, login, phonePrefix],
  );

  const handleOnSubmit = useCallback(
    async (data: RegisterFormType) => {
      setIsRegisterLoading(true);
      const { phone, sms: code, password, username } = data;

      const otpRes = await verifyOTP({
        phone: standardizePhoneNumber(phone, phonePrefix),
        code: code,
        accountId: envConfig.accountId,
      });

      handleApiResponse(otpRes, {
        toastError: false,
        toastSuccess: false,
        onError: (error) => {
          trackOTPFailedAttempt(error, { source: OTPSource.REGISTER });

          if (error?.data?.error?.message) {
            setError("sms", { message: error?.data?.error.message });
          }
        },
      });

      if (otpRes?.error) {
        setIsRegisterLoading(false);
        return;
      }

      // Track OTP verification success with analytics data
      trackOTPVerified(OTPMethod.SMS, {
        timeToVerify: otpSentTime.current ? Math.round((Date.now() - otpSentTime.current) / 1000) : undefined,
        source: OTPSource.REGISTER,
      });

      // Track form submission only after OTP verification success
      trackRegisterFormSubmit(RegistrationMethod.USERNAME_PASSWORD, {
        timeSpentOnForm: Math.round((Date.now() - formStartTime.current) / 1000),
      });

      // Call register
      const referralCode = getReferralCode();
      const standardizedPhone = standardizePhoneNumber(phone, phonePrefix);
      const seonSession = await getSeonSession();
      const registerRes = await register({
        accountId: envConfig.accountId,
        phone: standardizedPhone,
        password,
        accessKey: username.toLowerCase(),
        code,
        referralCode,
        seonSession: seonSession,
      });

      handleApiResponse(registerRes, {
        toastSuccess: false,
        toastError: false,
        onSuccess: () => {
          // Track registration completion with comprehensive analytics
          trackRegistrationComplete(RegistrationMethod.USERNAME_PASSWORD, {
            member: registerRes?.data?.member,
          });

          toast.success(t("Register successfully!"));
          if (referralCode) {
            clearReferralCode();
          }
          handleLogin(data.username, data.password);
          persistCredential({
            phone: standardizedPhone,
          });
          onRegisterSuccess?.();
          setIsRegisterLoading(false);
        },
        onError: async (error) => {
          const errorMessages = getErrorMessages(error);
          if (errorMessages.phone) {
            if (errorMessages.phone === APIErrorCodes.Member.PhoneConflict) {
              const success = await getUserDataForDialog(phone, code);
              if (success) {
                dispatch(hideOnboardingOverlay());
                setIsPhoneAlreadyRegisteredDialogOpen(true);
              }
            }
            setError("phone", { message: errorMessages.phone });
          }
          if (errorMessages.username) {
            setError("username", { message: errorMessages.username });
          }
          if (!errorMessages.username && !errorMessages.phone && error?.data?.error?.message) {
            toast.error(error?.data?.error?.message);
          }
          trackRegistrationFailed(RegistrationMethod.USERNAME_PASSWORD, error);
          setIsRegisterLoading(false);
        },
      });
    },
    [
      t,
      dispatch,
      getSeonSession,
      handleApiResponse,
      handleLogin,
      onRegisterSuccess,
      phonePrefix,
      register,
      setError,
      setIsPhoneAlreadyRegisteredDialogOpen,
      setIsRegisterLoading,
      trackOTPFailedAttempt,
      trackOTPVerified,
      trackRegisterFormSubmit,
      trackRegistrationComplete,
      trackRegistrationFailed,
      getUserDataForDialog,
      verifyOTP,
    ],
  );

  const handleClosePhoneExistDialog = () => {
    setIsPhoneAlreadyRegisteredDialogOpen(false);
    dispatch(showOnboardingOverlay());
  };

  const handleLoginDialog = async () => {
    if (authTokenRef.current) {
      dispatch(auth.slice.actions.updateAccessToken(authTokenRef.current));
      postLoginCheck(authTokenRef.current);

      setTimeout(() => {
        handleClosePhoneExistDialog();
      }, 500);
    }
  };
  const isLoading = isLoginLoading || isRegisterLoading;

  const handleSubmitOrGetCode = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const { sms } = getValues();

      // If OTP is empty, treat submit as a "Get code" action.
      if (!sms?.trim()) {
        // Prevent re-sending OTP during cooldown or while request is in-flight (Enter key can spam otherwise).
        if (isCreateRegisterLoading) return;
        if (Date.now() < otpCooldownUntilRef.current) return;
        void handleGetCode().catch(() => undefined);
        return;
      }

      // Otherwise, run full RHF validation + submit flow.
      void handleFormSubmit(handleOnSubmit)(e).catch(() => undefined);
    },
    [getValues, handleFormSubmit, handleGetCode, handleOnSubmit, isCreateRegisterLoading],
  );

  return (
    <>
      <form
        id="form-register-username-password"
        name="form-register-username-password"
        onSubmit={handleSubmitOrGetCode}
        className="flex w-full flex-col gap-y-3"
        autoComplete="off"
        onFocus={() => {
          if (!hasRegisterFormFocusedRef.current) {
            trackRegisterFormFocus(RegistrationMethod.USERNAME_PASSWORD);
            hasRegisterFormFocusedRef.current = true;
          }
        }}
      >
        <TextField
          {...formRegister("username")}
          error={!!formErrors.username}
          helperText={t(formErrors.username?.message as string)}
          placeholder={t("username")}
          icon={<MdPerson className="text-2xl text-content-base" />}
          classes={inputClasses}
          size="sm"
          autoComplete="new-password"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Register username input username-password"
          fieldHelperTextManager={fieldHelperTextManager}
        />
        <PasswordTextField
          {...formRegister("password")}
          error={!!formErrors.password}
          helperText={t(formErrors.password?.message as string)}
          placeholder={t("password")}
          classes={inputClasses}
          buttonClass="!top-[10px]"
          size="sm"
          autoComplete="new-password"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Register password input username-password"
          fieldHelperTextManager={fieldHelperTextManager}
        />
        <TextField
          {...formRegister("phone")}
          error={!!formErrors.phone}
          helperText={t(formErrors.phone?.message ?? "", {
            minLength: phoneMinLength,
            maxLength: phoneMaxLength,
            startWith: Array.isArray(phoneStartWith) ? phoneStartWith.join(", ") : "0",
          })}
          placeholder={t("Phone number")}
          type="number"
          inputMode="numeric"
          icon={<MdPhoneAndroid className="text-2xl text-content-base" />}
          classes={{
            ...inputClasses,
            "input&": inputClasses["input&"] + (showPhoneInputPrefix ? " !pl-20" : ""),
            "prefixContainer&": showPhoneInputPrefix ? "absolute left-11 top-[10px] text-content-base" : "",
          }}
          size="sm"
          autoComplete="new-password"
          aria-label="Register phone input username-password"
          prefix={showPhoneInputPrefix ? phonePrefix : undefined}
          fieldHelperTextManager={fieldHelperTextManager}
        />
        <div className="relative">
          <TextField
            {...formRegister("sms")}
            error={!!formErrors.sms && submitCount > 0}
            helperText={t(formErrors.sms?.message as string)}
            placeholder={t("enter code")}
            type="text"
            icon={<MdMail className="text-2xl text-content-base" />}
            classes={inputClasses}
            size="sm"
            autoComplete="one-time-code"
            inputMode="numeric"
            helperTextClasses={{
              classes: {
                "errorText&": "!h-4",
              },
            }}
            aria-label="Register code input username-password"
            fieldHelperTextManager={fieldHelperTextManager}
          />
          <GetCodeButton
            onGetCode={handleGetCode}
            className={cn("absolute right-3 top-2.5")}
            classes={{
              "root&": "w-fit px-2 !h-6",
            }}
            loading={isCreateRegisterLoading}
            ref={getCodeButtonRef}
            pulse={isGetCodeHintActive}
          />
          {formErrors.sms?.message === "otp is required. click on get code" && (
            <div
              className={cn(
                "text-right text-xs",
                submitCount > 0 && fieldHelperTextManager.shouldShowError("sms")
                  ? "text-error-base"
                  : "text-content-soft",
              )}
            >
              {t("didnt receive")} {""}
              <button
                type="button"
                onClick={handleOpenContactLink}
                className="underline"
                aria-label="Register contact cs username-password"
              >
                {t("contact cs")}
              </button>
            </div>
          )}
        </div>
        <Button
          variant="primary"
          type="submit"
          loading={isLoading}
          disabled={isLoading || isCreateRegisterLoading}
          size="lg"
          className="mt-2"
          aria-label="Register submit username-password"
        >
          {t("Next")}
        </Button>
      </form>

      {/* SOCIAL AUTH */}
      <AuthSocial
        mode="register"
        title={t("or")}
        onLoginSuccess={postLoginCheck}
        className="mt-2"
      />
      <PhoneAlreadyRegisteredDialog
        open={isPhoneAlreadyRegisteredDialogOpen}
        onClose={handleClosePhoneExistDialog}
        onLogin={handleLoginDialog}
        isLoading={isLoginLoading}
        profile={existingProfileDetails}
      />
    </>
  );
};

export default RegisterUP;
